import React, { useMemo } from 'react';
import FechaDesdeMixtoSelect from './FechaDesdeMixtoSelect';
import { esFechaIsoCompleta, fechaMitadPeriodo } from '../utils/pagoJornalPlanilla';

export default function PagoJornalModal({
  isOpen,
  onClose,
  onRecalcular,
  empleados = [],
  planillaNombre,
  fechaInicio,
  fechaFin,
  planillaCerrada = false,
  guardando = false,
  recalculando = false,
  formaPagoPlanillaDe,
  fechaDesdeJornalDe,
  descuentoDominicalDe,
  onCambiarFormaPago,
  onCambiarDescuentoDominical,
}) {
  const empleadosOrdenados = useMemo(
    () =>
      [...empleados].sort((a, b) =>
        String(a.nombre_completo || a.nombre || '').localeCompare(
          String(b.nombre_completo || b.nombre || ''),
          'es'
        )
      ),
    [empleados]
  );

  const cantidadOverride = empleadosOrdenados.filter((emp) => formaPagoPlanillaDe(emp.id)).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-emerald-600 text-white p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Pago por jornal</h2>
              <p className="text-emerald-100 text-sm mt-1">
                Planilla: {planillaNombre || '—'} | {empleadosOrdenados.length} trabajador(es)
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:text-emerald-100 transition-colors text-2xl leading-none"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-6">
            <p className="text-emerald-900 text-sm">
              Por defecto se usa la ficha del empleado. En jornal, la plantilla se divide entre los días
              del mes y se pagan asistencia, descanso, feriado no laborado y licencia con goce.
              Las vacaciones van al 0118, aparte del 0121. Faltas y permisos no generan descuento 0705.
              Dominical y tardanzas se muestran en planilla y se restan del 0121 al declarar.
              Mixto: hasta un día mensual y desde esa fecha jornal.
              El descuento dominical viene activo para todos; puedes quitarlo solo a quien no lo necesite.
            </p>
          </div>

          {cantidadOverride > 0 && (
            <div className="mb-4">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                {cantidadOverride} trabajador(es) con override en esta planilla
              </span>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Trabajador</th>
                  <th className="text-left px-4 py-3 font-medium w-32">Ficha</th>
                  <th className="text-left px-4 py-3 font-medium">En esta planilla</th>
                  <th className="text-left px-4 py-3 font-medium w-56">Descuento dominical</th>
                  <th className="text-left px-4 py-3 font-medium w-72">Desde (mixto)</th>
                </tr>
              </thead>
              <tbody>
                {empleadosOrdenados.map((emp) => {
                  const ficha = emp.forma_pago === 'jornal' ? 'jornal' : 'mensual';
                  const override = formaPagoPlanillaDe(emp.id);
                  const fecha = fechaDesdeJornalDe(emp.id);
                  const dominical = descuentoDominicalDe ? descuentoDominicalDe(emp.id) : '';
                  return (
                    <tr key={emp.id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {emp.nombre_completo || emp.nombre || 'Empleado'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {emp.numero_documento || emp.documento || emp.dni || ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {ficha === 'jornal' ? 'Jornal' : 'Mensual'}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="w-full min-w-[16rem] border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          value={override || ''}
                          disabled={guardando || planillaCerrada}
                          onChange={(e) => {
                            const siguiente = e.target.value || null;
                            const fechaMixto = siguiente === 'mixto'
                              ? (esFechaIsoCompleta(fecha)
                                ? fecha
                                : fechaMitadPeriodo(fechaInicio, fechaFin))
                              : fecha;
                            onCambiarFormaPago(emp.id, siguiente, fechaMixto);
                          }}
                        >
                          <option value="">
                            Heredar ficha ({ficha === 'jornal' ? 'jornal' : 'mensual'})
                          </option>
                          <option value="mensual">Mensual</option>
                          <option value="jornal">Jornal</option>
                          <option value="mixto">Mixto (hasta un día mensual, luego jornal)</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          value={dominical || ''}
                          disabled={guardando || planillaCerrada}
                          onChange={(e) => onCambiarDescuentoDominical?.(emp.id, e.target.value || null)}
                        >
                          <option value="">Según empresa (aplicar)</option>
                          <option value="si">Aplicar</option>
                          <option value="no">No aplicar</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {override === 'mixto' ? (
                          <FechaDesdeMixtoSelect
                            value={fecha}
                            min={fechaInicio}
                            max={fechaFin}
                            disabled={guardando || planillaCerrada}
                            onCommit={(iso) => onCambiarFormaPago(emp.id, 'mixto', iso)}
                          />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-8 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={guardando || recalculando}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cerrar
          </button>
          <button
            type="button"
            disabled={guardando || recalculando || planillaCerrada}
            onClick={async () => {
              onClose();
              if (onRecalcular) await onRecalcular();
            }}
            className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {recalculando ? 'Recalculando…' : 'Salir y recalcular'}
          </button>
        </div>
      </div>
    </div>
  );
}
